/**
 * Where a composer opens.
 *
 * Every route into writing a message goes through this module: the Compose
 * button, the reader's Reply, Reply-all and Forward, the swipe binding, the
 * keyboard, and a draft picked out of the Drafts mailbox. On the desktop each
 * of them raises a window of its own; in a browser each of them raises the
 * in-page composer, exactly as it always has.
 *
 * # Why the decision is here and not in `lib/compose.ts`
 *
 * It would be tidier to put it in the `open*` functions themselves, so that
 * every existing caller was routed without being changed. It cannot go there:
 * `lib/navigation.ts` owns the compose address grammar and already imports
 * `lib/compose.ts`, so a compose module that imported navigation back would
 * close a cycle -- the same cycle `lib/keys.ts` was carved out to avoid.
 *
 * So `lib/compose.ts` keeps the functions that open a composer **in this
 * window**, and this module is the layer above that decides whether "this
 * window" is the right one. The split is not accidental tidiness: the compose
 * window's own startup needs the in-page functions directly, because it *is*
 * the window that was opened and routing again would open a third.
 *
 * # A window is a separate JS context, and that is the whole difficulty
 *
 * Nothing is shared with the window that pressed the button -- not the signals,
 * not the store, not the draft. The new webview loads the same bundle from
 * scratch, works out from its URL what it was asked for, and fetches the
 * message itself. Everything the composer needs therefore has to survive a
 * round trip through a string, which is what `ComposeRequest` and
 * `composeFragmentFor` are for.
 *
 * That is also why a reply here does not carry the message it is replying to.
 * The main window has the full `Envelope` in hand and it is tempting to pass
 * it; there is nowhere to pass it *to*. The window gets an address and does its
 * own `Email/get`, which costs a round trip and is the only version that works.
 *
 * # Failing to open a window means composing in place
 *
 * Not an error toast and nothing else. Somebody pressed Compose, and a button
 * that reports a failure without producing a composer has lost them their
 * message either way. So the fallback is the browser's own behaviour -- the
 * composer as a layer over the mail -- with the failure said out loud beside
 * it, because a composer appearing somewhere unexpected needs an explanation or
 * it reads as a bug.
 */

import { multiWindow, openComposeWindow, windowRole } from "@transport";
import { signal } from "@preact/signals";

import * as compose from "./compose";
import { getEmail } from "./jmap";
import * as nav from "./navigation";
import * as store from "./store";
import type { EmailAddress, Envelope } from "./types";

/**
 * Whether a composer raised from here belongs in a window of its own.
 *
 * Both halves are needed and they are different questions. `multiWindow` is
 * whether this build has windows at all -- a browser does not, and the
 * in-page composer is its answer rather than a degraded version of one.
 * `windowRole()` is which window this is: a composer that opened a second
 * composer would be a compose window spawning compose windows, and a message
 * window's Reply button should raise its own rather than reach across to the
 * mail window's.
 *
 * Read rather than inferred, for the reason `lib/net.types.ts` sets out at
 * length: `windowRole()` answers `"main"` on both builds, and a caller that
 * branched on it alone would put a detached composer in the browser.
 */
function elsewhere(): boolean {
  return multiWindow && windowRole() !== "compose";
}

/**
 * Raise a composer for `request`, wherever it belongs.
 *
 * `here` is what the browser does and what the desktop falls back to. It is
 * passed in rather than derived, because only the caller still holds the things
 * an in-page composer wants -- the `Envelope` a reply quotes, the addresses a
 * blank message was prefilled with -- and a fallback that had to re-fetch them
 * would fail in exactly the situation it exists to rescue.
 */
async function raise(
  request: nav.ComposeRequest,
  // Awaited, so that this function resolving means there is a composer. Not
  // decoration: resuming a draft in place fetches the message first, and a
  // caller that could not wait for it would be told the composer was open
  // while it was still empty.
  here: () => void | Promise<void>,
): Promise<void> {
  if (!elsewhere()) {
    await here();
    return;
  }
  try {
    await openComposeWindow({
      identity: nav.composeIdentityFor(request),
      fragment: nav.composeFragmentFor(request),
    });
  } catch (e) {
    await here();
    store.toast(
      `Writing in this window — a separate one would not open (${
        e instanceof Error ? e.message : String(e)
      })`,
      "error",
    );
  }
}

/**
 * A blank message, or whatever an interrupted composer left unfinished.
 *
 * The in-page fallback is deliberately *not* `compose.openNew()` when there is
 * nothing to prefill. Raising the flag is what the Compose button has always
 * done in a browser, and it is not the same instruction: `openNew` resets the
 * draft, whereas this reveals the one that is already there -- so a composer
 * closed with a half-written message in it comes back with the message still in
 * it. Substituting the tidier-looking call would have thrown that away on the
 * build this feature was supposed to leave alone.
 */
export function writeNew(to: EmailAddress[] = []): Promise<void> {
  return raise(
    // Formatted rather than passed as objects, because the far side reads them
    // out of a URL: `formatAddress` and `parseAddress` are the pair that
    // survives that, and they are the same pair every recipient field uses.
    { kind: "new", address: null, to: to.map(compose.formatAddress) },
    () => {
      if (to.length > 0) compose.openNew(to);
      else store.composeOpen.value = true;
    },
  );
}

/**
 * The whole message, when the caller may only be holding a list row.
 *
 * A row from the message list was fetched with `LIST_PROPERTIES`, which has
 * no `messageId`, `references`, `cc`, `replyTo` or `bodyValues` -- the
 * `Email` type declares them all the same, so nothing complains. A reply
 * built from one goes out with no threading headers (a brand-new
 * conversation in every other client), drops the Cc list, and quotes an
 * attribution line over nothing. On the desktop the new window re-fetches by
 * address and never sees this; in a browser -- and on the desktop's
 * window-failed fallback -- the row is used directly, so it is completed
 * here first.
 *
 * `bodyValues` is the tell: it is fetched only ever on a full message. A
 * failed fetch falls back to the row itself, because a degraded reply the
 * user can see and fix beats a button that did nothing -- the same bargain
 * {@link raise} strikes when a window will not open.
 */
async function fullMessage(row: Envelope): Promise<Envelope> {
  if (row.email.bodyValues) return row;
  try {
    return await getEmail(row.accountId, row.email.id);
  } catch {
    return row;
  }
}

/** Reply to the sender, or to everyone on the message. */
export function reply(row: Envelope, all = false): Promise<void> {
  return raise(
    {
      kind: all ? "replyAll" : "reply",
      address: { account: row.accountId, email: row.email.id },
      to: [],
    },
    async () => compose.openReply(await fullMessage(row), all),
  );
}

/** Send this message on to somebody else. */
export function forward(row: Envelope): Promise<void> {
  return raise(
    { kind: "forward", address: { account: row.accountId, email: row.email.id }, to: [] },
    async () => compose.openForward(await fullMessage(row)),
  );
}

/**
 * Carry on writing a draft that is already on the server.
 *
 * Takes an address rather than a row, unlike its neighbours, and the asymmetry
 * is the point. A list row carries only what the list displays -- there are no
 * `bodyValues` on it -- so resuming a draft needs the whole message whichever
 * window ends up holding it. On the desktop the *new window* is the one that
 * fetches, so making the caller do it first would buy a round trip nobody
 * reads; in a browser there is no new window, so this fetches before opening.
 *
 * Adopting the draft's own id is what makes carrying on *replace* that message
 * rather than accumulate beside it. `compose.openDraft` does it; see
 * {@link compose.draftId} for what forgetting costs.
 */
export function resumeDraft(address: { account: string; email: string }): Promise<void> {
  return raise({ kind: "draft", address, to: [] }, () => fetchThenResume(address));
}

async function fetchThenResume(address: { account: string; email: string }): Promise<void> {
  try {
    compose.openDraft(await getEmail(address.account, address.email));
  } catch (e) {
    // Nothing has been lost -- the draft is where it was -- and saying where it
    // is matters more than the failure to fetch it. The same words
    // `ui/Outbox.tsx` uses for the same failure.
    store.toast(
      `That draft could not be opened — ${e instanceof Error ? e.message : String(e)}`,
      "error",
    );
  }
}

/**
 * Whether clicking this row should open a composer rather than the reader.
 *
 * Two ways to be a draft, and both are asked because neither is reliable alone.
 * The mailbox role is what a person means by "my Drafts folder", and it is the
 * answer for an account whose provider files drafts by folder. `$draft` is RFC
 * 8621's own flag and is what survives a message being filed somewhere else --
 * including into the folder the outbox parks a cancelled send in.
 *
 * Asked of the row rather than of the view, for the reason `store.roleOf` is:
 * a search returns drafts mixed in with everything else, and a unified view has
 * no single mailbox to ask about.
 */
export function isDraft(row: Envelope): boolean {
  const roles = rolesOf(row);
  // A message in Sent is not a draft, whatever else it is filed under, and this
  // clause is doing real work rather than guarding a hypothetical.
  //
  // `mutate.sendEmail` stages the outgoing message in the Drafts folder and
  // then files it with `onSuccessUpdateEmail`, which *adds* Sent and strips
  // `$draft` -- it does not remove the staging mailbox, deliberately, because
  // "append beside rather than relocate" is the failure that module is willing
  // to have on a provider that refuses the move. So after every send there is a
  // message sitting in both folders, and without this it would open in the
  // composer when somebody clicked it in Drafts: the client offering to carry
  // on writing a message it has already sent.
  if (roles.has("sent")) return false;
  return row.email.keywords["$draft"] === true || roles.has("drafts");
}

/**
 * Every role this message's folders carry.
 *
 * Deliberately not `store.roleOf`, which answers with the *first* role it finds
 * and is right for the question it was written for -- "which way does this row
 * point", asked of a message in one folder. It cannot answer this one: a
 * message in both Drafts and Sent has two roles, and which of them `roleOf`
 * returns depends on the iteration order of `mailboxIds`. A rule that read like
 * it was about roles and was actually about key order is the kind of wrong
 * answer that ships silently.
 */
function rolesOf(row: Envelope): Set<string> {
  const out = new Set<string>();
  for (const box of store.mailboxes.value.get(row.accountId) ?? []) {
    if (box.role !== null && row.email.mailboxIds[box.id]) out.add(box.role);
  }
  return out;
}

/**
 * Open a row the way it asks to be opened: the composer for a draft, the reader
 * for everything else.
 *
 * **For deliberate activation only** -- a click, `Enter`, `o`. Not for the
 * keyboard cursor: `j` and `k` open each row they pass over, and a Drafts
 * folder walked with them would raise a composer per message. `store.step`
 * therefore still calls `store.open` directly, which is the distinction between
 * moving through a list and choosing something in it.
 */
export function activate(row: Envelope): Promise<void> {
  if (isDraft(row)) return resumeDraft({ account: row.accountId, email: row.email.id });
  return store.open(row);
}

/**
 * Why this window's composer could not be filled in, or `null` while all is
 * well.
 *
 * Only ever set in a compose window, where a failure has nowhere to degrade to.
 * The main window can fail to open a composer and still be showing the mail;
 * this window would be showing an empty frame, so the reason has to be on
 * screen. See `ui/ComposeWindow.tsx` for what it renders and why a blank
 * composer is not an acceptable substitute.
 */
export const openFailure = signal<string | null>(null);

/**
 * Fill this window's composer from the address it was loaded at.
 *
 * The other end of every function above, and the only caller that must *not*
 * route: this runs in the window that was just opened, so `compose.openReply`
 * and its neighbours are called directly. Going back through {@link raise}
 * would open a third window and leave this one empty.
 */
export async function applyRequest(request: nav.ComposeRequest): Promise<void> {
  if (request.kind === "new") {
    const to = request.to
      .map((raw) => compose.parseAddress(raw))
      .filter((a): a is EmailAddress => a !== null);
    compose.openNew(to.length > 0 ? to : undefined);
    return;
  }

  // Checked rather than asserted. `composeFromLink` refuses a kind that needs a
  // message and has none, so this cannot fire from a parsed address -- but this
  // function is also reachable from anything else that builds a request, and a
  // blank composer where a reply was asked for is precisely the silent wrong
  // answer that grammar was written strictly to avoid.
  const at = request.address;
  if (!at) throw new Error(`a ${request.kind} needs a message and this one names none`);

  const row = await getEmail(at.account, at.email);
  switch (request.kind) {
    case "reply":
      await compose.openReply(row, false);
      break;
    case "replyAll":
      // Awaited so the identities are known before the recipients are
      // computed -- this window is brand new and has learned nothing yet.
      await compose.openReply(row, true);
      break;
    case "forward":
      compose.openForward(row);
      break;
    case "draft":
      compose.openDraft(row);
      break;
  }
}
